import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  BoxesIcon,
  CarFrontIcon,
  ClipboardListIcon,
  LayoutDashboardIcon,
  PackageIcon,
  PackagePlusIcon,
  PlusIcon,
  QrCodeIcon,
  ScanQrCodeIcon,
  Settings2Icon,
  ShoppingCartIcon,
  StickyNoteIcon,
  TruckIcon,
} from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useFormat } from "@/lib/format";
import { countInteraction } from "@/lib/interactions";
import { useModule } from "@/lib/modules";
import { useTRPC } from "@/lib/trpc";
import { FileImage } from "./file-image";
import { OrderStatusBadge, WorkOrderStatusBadge } from "./status";

const PaletteContext = createContext<{ open: () => void }>({ open: () => {} });

export function useCommandPalette() {
  return useContext(PaletteContext);
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  // For someone who shares usage statistics (ADR 0016): the palette opened.
  useEffect(() => {
    if (open) countInteraction("search", "palette");
  }, [open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const value = useMemo(() => ({ open: () => setOpen(true) }), []);
  return (
    <PaletteContext value={value}>
      {children}
      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content className="fixed inset-x-3 top-[8vh] z-50 mx-auto max-w-xl overflow-hidden rounded-2xl border border-border bg-surface-raised shadow-lg outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:top-[14vh]">
            <DialogPrimitive.Title className="sr-only">Search</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              Search and actions
            </DialogPrimitive.Description>
            {open ? <PaletteBody close={() => setOpen(false)} /> : null}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </PaletteContext>
  );
}

function PaletteBody({ close }: { close: () => void }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const labels = useModule("qrLabels");
  const navigate = useNavigate();
  const format = useFormat();
  const [query, setQuery] = useState("");
  const deferred = useDeferredValue(query.trim());
  // One search per time the palette opens, however much is typed.
  const searched = useRef(false);
  useEffect(() => {
    if (deferred && !searched.current) {
      searched.current = true;
      countInteraction("search", "search");
    }
  }, [deferred]);
  const { data } = useQuery({
    ...trpc.search.queryOptions({ query: deferred }),
    enabled: deferred.length > 0,
    placeholderData: (previous) => previous,
  });

  const go = useCallback(
    (to: string, search?: Record<string, string>) => {
      close();
      void navigate({ to, search });
    },
    [close, navigate],
  );

  const showResults = deferred.length > 0 && data;
  const nothing =
    showResults &&
    !data.parts.length &&
    !data.vehicles.length &&
    !data.workOrders.length &&
    !data.orders.length;

  return (
    <Command shouldFilter={!deferred} loop>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={t("palette.placeholder")}
        autoFocus
      />
      <CommandList>
        {nothing ? <CommandEmpty>{t("palette.empty")}</CommandEmpty> : null}
        {!deferred ? (
          <>
            <CommandGroup heading={t("palette.actions")}>
              <CommandItem onSelect={() => go("/inventory", { new: "1" })}>
                <PackagePlusIcon />
                {t("parts.new")}
              </CommandItem>
              <CommandItem onSelect={() => go("/work-orders", { new: "1" })}>
                <PlusIcon />
                {t("workOrders.new")}
              </CommandItem>
              <CommandItem onSelect={() => go("/vehicles", { new: "1" })}>
                <PlusIcon />
                {t("vehicles.new")}
              </CommandItem>
              <CommandItem onSelect={() => go("/orders", { new: "1" })}>
                <PlusIcon />
                {t("orders.new")}
              </CommandItem>
              <CommandItem onSelect={() => go("/orders", { purchase: "1" })}>
                <ShoppingCartIcon />
                {t("orders.quickPurchase")}
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading={t("palette.navigation")}>
              {(
                [
                  ["/", t("nav.overview"), LayoutDashboardIcon],
                  ["/inventory", t("nav.inventory"), BoxesIcon],
                  ["/vehicles", t("nav.vehicles"), CarFrontIcon],
                  ["/work-orders", t("nav.workOrders"), ClipboardListIcon],
                  ["/templates", t("nav.templates"), StickyNoteIcon],
                  ["/orders", t("nav.orders"), TruckIcon],
                  ...(labels
                    ? ([
                        ["/labels", t("nav.labels"), QrCodeIcon],
                        ["/qr", t("nav.scanner"), ScanQrCodeIcon],
                      ] as const)
                    : []),
                  ["/settings", t("nav.settings"), Settings2Icon],
                  ["/settings/packages", t("makePackages.title"), PackageIcon],
                ] as const
              ).map(([to, label, Icon]) => (
                <CommandItem key={to} onSelect={() => go(to)}>
                  <Icon />
                  {label}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        ) : null}
        {showResults && data.parts.length ? (
          <CommandGroup heading={t("palette.parts")}>
            {data.parts.map((part) => (
              <CommandItem
                key={part.id}
                value={`part-${part.id}`}
                onSelect={() => go(`/inventory/${part.id}`)}
              >
                <FileImage
                  fileId={part.coverFileId}
                  alt=""
                  icon={BoxesIcon}
                  className="size-8 rounded-md"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{part.name}</div>
                  {part.primaryNumber ? (
                    <div className="truncate font-mono text-xs text-muted-foreground">
                      {part.primaryNumber}
                    </div>
                  ) : null}
                </div>
                <span className="text-xs text-muted-foreground tabular">
                  {t("palette.onHand", { qty: format.qty(part.onHand, part.unit) })}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {showResults && data.vehicles.length ? (
          <CommandGroup heading={t("palette.vehicles")}>
            {data.vehicles.map((vehicle) => (
              <CommandItem
                key={vehicle.id}
                value={`vehicle-${vehicle.id}`}
                onSelect={() => go(`/vehicles/${vehicle.id}`)}
              >
                <FileImage
                  fileId={vehicle.coverFileId}
                  alt=""
                  icon={CarFrontIcon}
                  className="size-8 rounded-md"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{vehicle.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{vehicle.subtitle}</div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {showResults && data.workOrders.length ? (
          <CommandGroup heading={t("palette.workOrders")}>
            {data.workOrders.map((wo) => (
              <CommandItem
                key={wo.id}
                value={`wo-${wo.id}`}
                onSelect={() => go(`/work-orders/${wo.id}`)}
              >
                <ClipboardListIcon />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{wo.title}</div>
                  <div className="truncate text-xs text-muted-foreground">{wo.vehicleName}</div>
                </div>
                <WorkOrderStatusBadge status={wo.status} />
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {showResults && data.orders.length ? (
          <CommandGroup heading={t("palette.orders")}>
            {data.orders.map((order) => (
              <CommandItem
                key={order.id}
                value={`order-${order.id}`}
                onSelect={() => go(`/orders/${order.id}`)}
              >
                <TruckIcon />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{order.name}</div>
                  {order.supplierName ? (
                    <div className="truncate text-xs text-muted-foreground">
                      {order.supplierName}
                    </div>
                  ) : null}
                </div>
                <OrderStatusBadge status={order.status} />
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </Command>
  );
}
